import { Box, type BoxProps, type SpaceProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type PropsWithChildren } from "react";
import { type BrandColorVariants, getBrandColorHex } from "theme/colors";
import { spaceInPx } from "theme/scale";

export type AuraPlateProps = PropsWithChildren<{
  /**
   * The color theme for the plate.
   * Determines which background, text, and aura colors are used.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The color variant for the plate.
   */
  variant: keyof BrandColorVariants;

  /**
   * Displays a version with narrower spacing around the plate layers.
   */
  condensed?: boolean;

  /**
   * Max width for the plate elements (excluding the space the outer aura takes up).
   */
  plateMaxW?: BoxProps["maxW"];

  /**
   * Horizontal padding for the innermost plate that directly wraps the child elements.
   */
  platePx?: BoxProps["px"];

  /**
   * Vertical padding for the innermost plate that directly wraps the child elements.
   */
  platePy?: BoxProps["px"];
}> &
  SpaceProps;

export function AuraPlate({
  theme = "light",
  variant,
  condensed,
  children,
  plateMaxW,
  platePx,
  platePy,
  ...spaceProps
}: AuraPlateProps) {
  let bgColor;
  let outerShadow;
  let innerShadow;
  let outerPadding: BoxProps["py"] = spaceInPx.biggest;

  if (theme === "light") {
    bgColor = getBrandColorHex(variant, 150);
    const shadowColor = "white";

    outerShadow = {
      // Use a slightly smaller/less intense outer shadow for mobile screens
      base: `0px 0px 52px 12px ${shadowColor}, 0px 0px 40px 32px ${bgColor}`,
      md: condensed
        ? `0px 0px 52px 16px ${shadowColor}, 0px 0px 44px 52px ${bgColor}`
        : `0px 0px 52px 16px ${shadowColor}, 0px 0px 70px 48px ${bgColor}`,
    };
    innerShadow = `0px 0px 52px 16px ${shadowColor}`;

    if (!condensed) {
      outerPadding = { base: spaceInPx.biggest, md: spaceInPx.huge };
    }
  } else {
    bgColor = getBrandColorHex(variant, 950);
    const shadowColor = getBrandColorHex(variant, 900);

    outerShadow = `0px 0px 38px 0px ${shadowColor}`;
    innerShadow = `0px 0px 38px 8px ${shadowColor}`;
  }

  return (
    <Box
      as="section"
      w="100%"
      px={{
        base: 0,
        sm: spaceInPx.smallest,
        md: condensed ? "52px" : spaceInPx.biggest,
        xl: condensed ? "52px" : spaceInPx.huge,
      }}
      py={outerPadding}
      {...spaceProps}
    >
      <Box
        w={{ base: "100%", md: "100%" }}
        mx={{ base: "auto", md: "auto" }}
        maxW={plateMaxW}
        borderRadius={{ base: spaceInPx.bigger, sm: spaceInPx.huge, xl: condensed ? spaceInPx.huge : spaceInPx.huger }}
        py={{
          base: spaceInPx.big,
          md: condensed ? spaceInPx.bigger : spaceInPx.biggest,
          xl: condensed ? spaceInPx.bigger : "110px",
        }}
        px={{
          base: spaceInPx.smallest,
          sm: "28px", // Need a slightly in-between size for this to feel balanced on sm screens
          md: condensed ? spaceInPx.bigger : spaceInPx.biggest,
          xl: condensed ? spaceInPx.bigger : "110px",
        }}
        bg={bgColor}
        color={`brand.text.${theme}`}
        boxShadow={outerShadow}
      >
        <Box
          as="section"
          borderRadius={{ base: spaceInPx.bigger, sm: spaceInPx.biggest }}
          px={platePx ?? { base: spaceInPx.big, sm: spaceInPx.biggest, xl: spaceInPx.huge }}
          py={platePy ?? { base: spaceInPx.biggest, md: spaceInPx.huge }}
          bg={bgColor}
          boxShadow={innerShadow}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
